import React, { useEffect, useRef, useState } from 'react';
import ReactDOM from 'react-dom';
import * as d3 from 'd3';
import styles from './SunburstChart.module.scss';

interface SunburstNode {
  name: string;
  value?: number;
  children?: SunburstNode[];
  color?: string;
  month?: string;
  description?: string;
  url?: string;
}

interface ExtendedHierarchyNode extends d3.HierarchyNode<SunburstNode> {
  current?: any;
  target?: any;
  x0?: number;
  x1?: number;
  y0?: number;
  y1?: number;
}

interface TooltipLabels {
  frequency: string;
  province: string;
  month: string;
}

interface SunburstChartProps {
  data: SunburstNode;
  width?: number;
  height?: number;
  tooltipLabels?: TooltipLabels;
  onProvinceSelect?: (provinceName: string | null, description?: string, url?: string) => void;
}

const SunburstChart: React.FC<SunburstChartProps> = ({ 
  data, 
  width: propWidth, 
  height: propHeight,
  tooltipLabels = {
    frequency: 'Frequency',
    province: 'Province',
    month: 'Month'
  },
  onProvinceSelect
}) => {
  const svgRef = useRef<SVGSVGElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);
  const [selectedNode, setSelectedNode] = useState<any>(null);
  const [dimensions, setDimensions] = useState({ width: propWidth || 600, height: propHeight || 600 });
  const selectedNodeRef = useRef<any>(null);
  const [isMobile, setIsMobile] = useState(false);

  useEffect(() => {
    if (!containerRef.current) return;
    
    const updateDimensions = () => {
      const container = containerRef.current;
      if (container) {
        const { width, height } = container.getBoundingClientRect();
        setDimensions({ 
          width: propWidth || width, 
          height: propHeight || height 
        });
      }
      setIsMobile(window.innerWidth <= 768);
    };
    
    updateDimensions();
    window.addEventListener('resize', updateDimensions);
    return () => window.removeEventListener('resize', updateDimensions);
  }, [propWidth, propHeight]);

  useEffect(() => {
    if (!svgRef.current || !data) return;

    d3.select(svgRef.current).selectAll('*').remove();
    
    selectedNodeRef.current = null;
    setSelectedNode(null);

    const { width, height } = dimensions;
    const padding = isMobile ? 10 : 20;
    const radius = Math.min(width - padding * 2, height - padding * 2) / 2;
    
    // 增加 viewBox 的边距，防止裁切（移动端使用较小的边距让图表更大）
    const viewBoxPadding = isMobile ? 15 : 20;
    const viewBoxWidth = width + viewBoxPadding * 2;
    const viewBoxHeight = height + viewBoxPadding * 2;
    
    const svg = d3.select(svgRef.current)
      .attr('width', '100%')
      .attr('height', '100%')
      .attr('viewBox', `${-viewBoxPadding} ${-viewBoxPadding} ${viewBoxWidth} ${viewBoxHeight}`)
      .attr('preserveAspectRatio', 'xMidYMid meet')
      .style('font', '12px sans-serif');
    
    const g = svg.append('g')
      .attr('transform', `translate(${width / 2}, ${height / 2})`);

    const root = d3.hierarchy(data)
      .sum((d: any) => d.value || 0) as ExtendedHierarchyNode;

    const centerRadius = 50;
    const partition = d3.partition<SunburstNode>()
      .size([2 * Math.PI, radius]);

    partition(root);
    
    let minY0 = Infinity;
    let maxY1Depth1 = 0;
    root.each((d: any) => {
      if (d.depth === 1) {
        if (d.y0 < minY0) minY0 = d.y0;
        if (d.y1 > maxY1Depth1) maxY1Depth1 = d.y1;
      }
    });
    
    const originalInnerWidth = maxY1Depth1 - minY0;
    const originalOuterWidth = radius - maxY1Depth1;
    
    const innerWidthIncrease = originalInnerWidth * 0.3;
    
    const offset = centerRadius - minY0;
    
    root.each((d: any) => {
      if (d.depth > 0) {
        d.y0 = d.y0 + offset;
        d.y1 = d.y1 + offset;
        
        if (d.depth === 1) {
          d.y1 = d.y1 + innerWidthIncrease;
        }
        else if (d.depth === 2) {
          d.y0 = d.y0 + innerWidthIncrease;
          d.y1 = d.y1 + innerWidthIncrease;
        }
      }
    });

    const arc = d3.arc<any>()
      .startAngle(d => d.x0)
      .endAngle(d => d.x1)
      .padAngle(d => Math.min((d.x1 - d.x0) / 2, 0.005))
      .padRadius(radius / 2)
      .innerRadius(d => d.y0 - 1)
      .outerRadius(d => d.y1 - 1);

    const color = d3.scaleOrdinal(d3.schemeCategory10);

    root.each((d: any) => {
      d.current = {
        x0: d.x0,
        x1: d.x1,
        y0: d.y0,
        y1: d.y1
      };
    });

    const showTooltip = (event: any, d: any) => {
      if (window.innerWidth <= 768 || !tooltipRef.current) return;
      
      const tooltip = tooltipRef.current;
      const isInnerRing = d.depth === 1;
      const isOuterRing = d.depth === 2;
      
      if (!isInnerRing && !isOuterRing) return;
      
      const provinceName = isInnerRing ? d.data.name : d.parent?.data.name || '';
      const categoryName = d.data.name;
      const month = isInnerRing ? (d.data.month || '') : (d.parent?.data.month || '');
      
      let tooltipHTML = '';
      if (isInnerRing) {
        tooltipHTML = `
          <div class="${styles.tooltipTitle}">${categoryName}</div>
          <div class="${styles.tooltipRow}">
            <span class="${styles.tooltipLabel}">${tooltipLabels.month}</span>
            <span class="${styles.tooltipValue}">${month}</span>
          </div>
        `;
      } else {
        const frequency = d.data.value || 0;
        tooltipHTML = `
          <div class="${styles.tooltipTitle}">${categoryName}</div>
          <div class="${styles.tooltipRow}">
            <span class="${styles.tooltipLabel}">${tooltipLabels.frequency}</span>
            <span class="${styles.tooltipValue}">${frequency}</span>
          </div>
          <div class="${styles.tooltipRow}">
            <span class="${styles.tooltipLabel}">${tooltipLabels.month}</span>
            <span class="${styles.tooltipValue}">${month}</span>
          </div>
        `;
      }

       {/* <div class="${styles.tooltipRow}">
            <span class="${styles.tooltipLabel}">${tooltipLabels.province}</span>
            <span class="${styles.tooltipValue} ${styles.tooltipValueProvince}">${provinceName}</span>
          </div> */}
      
      tooltip.innerHTML = tooltipHTML;
      tooltip.style.display = 'block';
      tooltip.style.opacity = '1';
      
      const offsetX = 15;
      const offsetY = 15;
      
      const tooltipRect = tooltip.getBoundingClientRect();
      const tooltipWidth = tooltipRect.width || 200;
      const tooltipHeight = tooltipRect.height || 100;
      
      let left = event.clientX + offsetX;
      let top = event.clientY + offsetY;
      
      if (left + tooltipWidth > window.innerWidth) {
        left = event.clientX - tooltipWidth - offsetX;
      }
      
      if (top + tooltipHeight > window.innerHeight) {
        top = event.clientY - tooltipHeight - offsetY;
      }
      
      left = Math.max(10, left);
      top = Math.max(10, top);
      
      tooltip.style.left = `${left}px`;
      tooltip.style.top = `${top}px`;
    };
    
    const hideTooltip = () => {
      if (!tooltipRef.current) return;
      tooltipRef.current.style.opacity = '0';
      setTimeout(() => {
        if (tooltipRef.current) {
          tooltipRef.current.style.display = 'none';
        }
      }, 200);
    };

    const path = g.append('g')
      .selectAll('path')
      .data(root.descendants().filter(d => d.depth) as ExtendedHierarchyNode[])
      .join('path')
      .attr('fill', (d: any) => {
        if (d.depth === 1) {
          return d.data.color || color(d.data.name);
        }
        
        if (d.depth === 2 && d.parent) {
          return d.parent.data.color || color(d.parent.data.name);
        }
        
        return color(d.data.name);
      })
      .attr('fill-opacity', (d: any) => {
        if (!arcVisible(d.current)) return 0;
        return d.depth === 1 ? 1 : 0.85;
      })
      .attr('d', (d: any) => arc(d.current))
      .style('cursor', 'pointer')
      .on('click', clicked)
      .on('mouseenter', function(event: any, d: any) {
        // 移动端不处理 hover 效果，避免与点击冲突
        if (isMobile) return;
        
        if (d.depth === 1 && selectedNodeRef.current === d) {
          d3.select(this).attr('fill-opacity', 0.5);
          centerCircle.attr('fill-opacity', 0.5);
        }
        showTooltip(event, d);
      })
      .on('mousemove', function(event: any, d: any) {
        // 移动端不处理 hover 效果
        if (isMobile) return;
        showTooltip(event, d);
      })
      .on('mouseleave', function(event: any, d: any) {
        // 移动端不处理 hover 效果
        if (isMobile) return;
        
        if (d.depth === 1 && selectedNodeRef.current === d) {
          d3.select(this).attr('fill-opacity', 1);
          centerCircle.attr('fill-opacity', 1);
        }
        hideTooltip();
      });

    const label = g.append('g')
      .attr('pointer-events', 'none')
      .attr('text-anchor', 'middle')
      .style('user-select', 'none')
      .selectAll('g')
      .data(root.descendants().filter((d: any) => d.depth) as ExtendedHierarchyNode[])
      .join('g')
      .attr('transform', (d: any) => labelTransform(d.current, d))
      .attr('opacity', (d: any) => labelVisible(d.current) ? 1 : 0)
      .each(function(d: any) {
        renderLabel(d3.select(this), d, false);
      });

    const centerGroup = g.append('g')
      .attr('class', 'center-group');
    
    const centerCircle = centerGroup.append('circle')
      .datum(root)
      .attr('r', 50)
      .attr('fill', '#f5f5f5')
      .attr('fill-opacity', 1)
      .attr('stroke', 'none')
      .attr('pointer-events', 'all')
      .style('cursor', 'pointer')
      .on('click', clicked)
      .on('mouseenter', function() {
        // 移动端不处理 hover 效果
        if (isMobile) return;
        
        if (selectedNodeRef.current) {
          d3.select(this).attr('fill-opacity', 0.5);
          path.filter((d: any) => d === selectedNodeRef.current)
            .attr('fill-opacity', 0.5);
        }
      })
      .on('mouseleave', function() {
        // 移动端不处理 hover 效果
        if (isMobile) return;
        
        if (selectedNodeRef.current) {
          d3.select(this).attr('fill-opacity', 1);
          path.filter((d: any) => d === selectedNodeRef.current)
            .attr('fill-opacity', 1);
        }
      });
    
    const centerTextGroup = centerGroup.append('g')
      .attr('class', 'center-text-group')
      .attr('text-anchor', 'middle')
      .attr('opacity', 0)
      .style('pointer-events', 'none');

    function clicked(_event: any, p: any) {
      const currentSelected = selectedNodeRef.current;
      
      const isResetting = p.depth === 0 || (currentSelected && (p.depth === 1 || p.depth === 2));
      
      if (isResetting) {
        p = root;
        selectedNodeRef.current = null;
        setSelectedNode(null);
        // 通知父组件重置选择
        if (onProvinceSelect) {
          onProvinceSelect(null);
        }
      } else {
        if (p.depth === 1) {
          selectedNodeRef.current = p;
          setSelectedNode(p);
          // 通知父组件省份被选中
          if (onProvinceSelect) {
            const provinceData = data.children?.find(child => child.name === p.data.name);
            onProvinceSelect(p.data.name, provinceData?.description, provinceData?.url);
          }
        } else if (p.depth === 2) {
          selectedNodeRef.current = p.parent;
          setSelectedNode(p.parent);
          p = p.parent;
          // 通知父组件省份被选中
          if (onProvinceSelect) {
            const provinceData = data.children?.find(child => child.name === p.data.name);
            onProvinceSelect(p.data.name, provinceData?.description, provinceData?.url);
          }
        } else {
          return;
        }
      }

      centerCircle.datum(p.parent || root);

      root.each((d: any) => {
        d.target = {
          x0: Math.max(0, Math.min(1, (d.x0 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          x1: Math.max(0, Math.min(1, (d.x1 - p.x0) / (p.x1 - p.x0))) * 2 * Math.PI,
          y0: Math.max(0, d.y0 - p.depth),
          y1: Math.max(0, d.y1 - p.depth)
        };
      });

      const t = g.transition().duration(750);

      if (isResetting) {
        label.filter((d: any) => d.depth === 2)
          .attr('opacity', 0)
          .each(function(d: any) {
            d3.select(this as SVGGElement).selectAll('text').remove();
          });
      }

      path.transition(t as any)
        .tween('data', (d: any) => {
          const i = d3.interpolate(d.current, d.target);
          return (t: number) => d.current = i(t);
        })
        .filter(function(d: any) {
          const opacity = (this as SVGPathElement).getAttribute('fill-opacity');
          return Boolean((opacity ? +opacity : 0) || arcVisible(d.target));
        })
        .attr('fill-opacity', (d: any) => {
          if (!arcVisible(d.target)) return 0;
          return d.depth === 1 ? 1 : 0.7;
        })
        .attrTween('d', (d: any) => () => arc(d.current) as string);

      label.filter(function(d: any) {
        const opacity = (this as SVGGElement).getAttribute('opacity');
        return Boolean((opacity ? +opacity : 0) || labelVisible(d.target));
      })
        .transition(t as any)
        .attr('opacity', (d: any) => {
          if (!isResetting && p.depth === 1 && d === p) {
            return 0;
          }
          return +labelVisible(d.target);
        })
        .attrTween('transform', (d: any) => () => labelTransform(d.current, d) as string)
        .on('end', function(d: any) {
          const isExpanded = !isResetting && p.depth === 1;
          
          d3.select(this as SVGGElement)
            .attr('transform', labelTransform(d.current, d));
          
          renderLabel(d3.select(this as SVGGElement), d, isExpanded);
        });
      
      if (!isResetting && p.depth === 1) {
        let nodeColor = p.data.color;
        if (!nodeColor) {
          let colorNode = p;
          while (colorNode.depth > 1) colorNode = colorNode.parent!;
          nodeColor = color(colorNode.data.name);
        }
        
        centerCircle.transition(t as any)
          .attr('fill', nodeColor);
        
        renderCenterText(centerTextGroup, p.data.name, 50);
        centerTextGroup
          .transition(t as any)
          .attr('opacity', 1);
      } else {
        centerCircle.transition(t as any)
          .attr('fill', '#f5f5f5');
        
        centerTextGroup.transition(t as any)
          .attr('opacity', 0);
      }
    }

    function arcVisible(d: any) {
      return d.y1 > d.y0 && d.x1 > d.x0;
    }

    function labelVisible(d: any) {
      if (d.depth === 1) {
        return d.y1 > d.y0 && (d.x1 - d.x0) > 0.05;
      }
      if (d.depth === 2) {
        const isExpanded = selectedNodeRef.current !== null;
        if (!isExpanded) {
          return false;
        }
        return d.y1 > d.y0 && (d.y1 - d.y0) > 5 && (d.x1 - d.x0) > 0.02;
      }
      return d.y1 > d.y0 && (d.x1 - d.x0) > 0.02;
    }

    function renderCenterText(group: any, text: string, maxRadius: number) {
      group.selectAll('*').remove();
      
      const fontSize = 16;
      const lineHeight = fontSize * 1.3;
      const maxWidth = maxRadius * 1.8;
      const maxCharsPerLine = Math.floor(maxWidth / (fontSize * 0.6));
      
      const words = text.split(/\s+/);
      const lines: string[] = [];
      let currentLine = '';
      
      words.forEach((word: string) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            lines.push(word);
            currentLine = '';
          }
        }
      });
      if (currentLine) lines.push(currentLine);
      
      const totalHeight = lines.length * lineHeight;
      const startY = -totalHeight / 2 + lineHeight / 2;
      
      lines.forEach((line: string, i: number) => {
        group.append('text')
          .attr('dy', `${startY + i * lineHeight}px`)
          .attr('fill', '#ffffff')
          .attr('font-size', `${fontSize}px`)
          .attr('font-weight', '700')
          .text(line);
      });
    }
    
    function labelTransform(dCurrent: any, dOriginal?: any) {
      const x = (dCurrent.x0 + dCurrent.x1) / 2 * 180 / Math.PI;
      const y = (dCurrent.y0 + dCurrent.y1) / 2;
      
      const d = dOriginal || dCurrent;
      
      if (d.depth === 1) {
        const angle = x - 90;
        const textRotation = (x >= 180 && x < 360) ? 180 : 0;
        return `rotate(${angle}) translate(${y},0) rotate(${textRotation})`;
      }
      
      if (d.depth === 2) {
        const isExpanded = selectedNodeRef.current !== null;
        
        if (isExpanded) {
          const angle = x - 90;
          const textRotation = (x > 90 && x < 270) ? -90 : 90;
          return `rotate(${angle}) translate(${y},0) rotate(${textRotation})`;
        }
        
        return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
      }
      
      return `rotate(${x - 90}) translate(${y},0) rotate(${x < 180 ? 0 : 180})`;
    }
    
    function renderLabel(group: any, d: any, isExpanded: boolean = false) {
      group.selectAll('text').remove();
      
      let text = d.data.name;
      const isInner = d.depth === 1;
      const isOuter = d.depth === 2;
      
      if (isOuter && !isExpanded) {
        return;
      }
      
      if (isInner) {
        if (text === 'Xinjiang Uygur Autonomous Region') {
          text = 'Xinjiang';
        } else if (text === 'Xizang Autonomous Region') {
          text = 'Xizang';
        }
      }
      
      const arcLength = (d.current.x1 - d.current.x0) * (d.current.y0 + d.current.y1) / 2;
      const radialWidth = d.current.y1 - d.current.y0;
      
      let baseFontSize = isInner ? 14 : 12;
      let minFontSize = isInner ? 10 : 7;
      
      if (isMobile) {
        baseFontSize = isInner ? 11 : 8;
        minFontSize = isInner ? 9 : 7;
      } else {
        // 大屏优化：根据视窗宽度动态调整字号
        const viewportWidth = window.innerWidth;
        if (viewportWidth >= 1920) {
          // 2K 及以上屏幕
          baseFontSize = isInner ? 18 : 14;
          minFontSize = isInner ? 14 : 10;
        } else if (viewportWidth >= 1440) {
          // 1440p 屏幕
          baseFontSize = isInner ? 16 : 13;
          minFontSize = isInner ? 12 : 9;
        }
        // 1080p 及以下保持原有的 14/12
      }
      
      let fontSize = baseFontSize;
      const useHorizontalLayout = isOuter && selectedNodeRef.current !== null;
      
      if (isInner) {
        const estimatedWidth = text.length * fontSize * 0.6;
        if (estimatedWidth > arcLength) {
          fontSize = Math.max(minFontSize, Math.floor(arcLength / (text.length * 0.6)));
        }
      } else if (useHorizontalLayout) {
        fontSize = Math.min(fontSize, 12);
      } else {
        const estimatedHeight = text.length * fontSize * 0.8;
        if (estimatedHeight > radialWidth) {
          fontSize = Math.max(minFontSize, Math.floor(radialWidth / (text.length * 0.8)));
        }
      }
      
      const fontWeight = isInner ? '700' : '400';
      
      let maxCharsPerLine: number;
      if (isInner) {
        maxCharsPerLine = Math.floor(arcLength / (fontSize * 0.6));
      } else if (useHorizontalLayout) {
        const calculatedMax = Math.floor(radialWidth / (fontSize * 0.3));
        maxCharsPerLine = calculatedMax;
      } else {
        maxCharsPerLine = Math.floor(radialWidth / (fontSize * 0.8));
      }
      
      if (text.length <= maxCharsPerLine || maxCharsPerLine < 3) {
        group.append('text')
          .attr('dy', '0.35em')
          .attr('fill', '#ffffff')
          .attr('font-size', `${fontSize}px`)
          .attr('font-weight', fontWeight)
          .text(text);
        return;
      }
      
      const words = text.split(/\s+/);
      const lines: string[] = [];
      let currentLine = '';
      
      words.forEach((word: string) => {
        const testLine = currentLine ? `${currentLine} ${word}` : word;
        if (testLine.length <= maxCharsPerLine) {
          currentLine = testLine;
        } else {
          if (currentLine) {
            lines.push(currentLine);
            currentLine = word;
          } else {
            lines.push(word);
            currentLine = '';
          }
        }
      });
      if (currentLine) lines.push(currentLine);
      
      let maxLines: number;
      if (isMobile && isOuter) {
        maxLines = useHorizontalLayout ? 6 : 1;
      } else if (useHorizontalLayout) {
        maxLines = 6;
      } else if (isInner) {
        maxLines = 3;
      } else {
        maxLines = 0;
      }
      
      const displayLines = lines.slice(0, maxLines);
      
      const lineHeight = fontSize * 1.3;
      const totalHeight = displayLines.length * lineHeight;
      const startY = -totalHeight / 2 + lineHeight / 2;
      
      displayLines.forEach((line: string, i: number) => {
        group.append('text')
          .attr('dy', `${startY + i * lineHeight}px`)
          .attr('fill', '#ffffff')
          .attr('font-size', `${fontSize}px`)
          .attr('font-weight', fontWeight)
          .text(line);
      });
    }

  }, [data, dimensions, tooltipLabels, isMobile]);

  return (
    <>
      <div ref={containerRef} className={styles.sunburstContainer}>
        <svg ref={svgRef} className={styles.sunburstSvg} />
        {/* {selectedNode && selectedNode.depth > 0 && (
          <div className={styles.info}>
            <h3>{selectedNode.data.name}</h3>
            {selectedNode.data.value && <p>Value: {selectedNode.data.value}</p>}
          </div>
        )} */}
      </div>
      {/* Tooltip - 使用 Portal 渲染到 body，避免父元素 transform 影响 */}
      {typeof document !== 'undefined' && ReactDOM.createPortal(
        <div ref={tooltipRef} className={styles.tooltip} />,
        document.body
      )}
    </>
  );
};

export default SunburstChart;
